import { Chain, Network, Wallet } from "mintbase"
import React, {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useContext,
} from "react"

interface IWalletProvider {
  network?: Network
  chain?: Chain
  apiKey: string
  children?: ReactNode
}

interface IWalletConsumer {
  wallet: Wallet | undefined
  details: {
    accountId: string
    balance: string
    allowance: string
    contractName: string
  }
}

export const WalletContext = createContext<{
  wallet: Wallet | undefined
  details: {
    accountId: string
    balance: string
    allowance: string
    contractName: string
  }
  isLoading: boolean
}>({
  wallet: undefined,
  details: {
    accountId: "",
    balance: "",
    allowance: "",
    contractName: "",
  },
  isLoading: true,
})

export const WalletProvider = (props: IWalletProvider) => {
  const { network, chain, apiKey, children } = props

  const [wallet, setWallet] = useState<Wallet | undefined>()
  const isConnected = wallet?.isConnected

  const [details, setDetails] = useState<{
    accountId: string
    balance: string
    allowance: string
    contractName: string
  }>({
    accountId: "",
    balance: "",
    allowance: "",
    contractName: "",
  })

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const initWallet = async () => {
      const { data: walletData, error } = await new Wallet().init({
        networkName:
          (process.env.GATSBY_WALLET_NETWORK_NAME as Network) ||
          Network.testnet,
        chain: chain ?? Chain.near,
        apiKey: process.env.GATSBY_MINTBASE_API_KEY,
      })

      if (error) {
        console.error(error)
        return
      }

      setWallet(walletData.wallet)
      setIsLoading(false)
    }

    initWallet()
  }, [network, apiKey, chain])

  useEffect(() => {
    const setConnectionStatus = async () => {
      if (isConnected) {
        try {
          const { data } = await wallet.details()
          setDetails(data)
        } catch (err) {
          console.error(err)
        }
      }
    }

    setConnectionStatus()
  }, [wallet, isConnected])

  return (
    <WalletContext.Provider
      // Eslint wants us to memoize the Provider's value here to avoid
      // constant re-renders when the value changes.
      // However, as we don't expect constant value-changes we don't need
      // need to optimize it
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{ wallet, details, isLoading }}
    >
      {children}
    </WalletContext.Provider>
  )
}

export const useWallet = () =>
  useContext<IWalletConsumer & { isLoading: boolean }>(WalletContext)
